import React, { useEffect, useMemo, useState } from "react";
import { Plus, Minus, Info, Star, ImageOff } from "lucide-react";
import { cartLineKey } from "../../../lib/retail/cartLineKey";
import {
  formatMoney, dietaryBadgesFor, formatPackLabel, spiceIndicatorFor,
  cleanImageUrl, SPICE_LABELS, SPICE_SHORT,
} from "../../../lib/retailFormat";

// Phase 3M — extracted verbatim from RetailCustomer.jsx::ProductCard.
// Renders a single menu card with badges, image, qty-stepper (or ADD), and
// info-modal trigger. Behaviour/testids identical.
//
// Customer Favorites (Aug 2026) — `favoriteState` is one of:
//   undefined/null    -> no star rendered at all — Favorites isn't
//                         offered on this tenant/catalog at all.
//   "verify"          -> star renders but clicking it starts OTP
//                         verification (`onRequireVerification`) — the
//                         customer has not verified their phone/email
//                         at all yet this session (Aug 2026 correction:
//                         genuinely distinct from "locked" below, which
//                         implies verification already happened).
//   "locked"          -> star renders but is a membership-invitation
//                         trigger (`onRequireMembership`), never a live
//                         toggle — used for a VERIFIED non-member.
//   "empty" | "filled" -> a live, member-usable toggle.
// `onToggleFavorite(product)` is called only for "empty"/"filled".
// `onRequireMembership()` is called only for "locked";
// `onRequireVerification()` (falling back to `onRequireMembership` if
// not supplied) only for "verify". Star click always stops propagation
// so it can never trigger the card's own add-to-cart or info-modal
// actions (RapidClickGuard below also protects against a duplicate
// in-flight toggle from rapid repeated clicks).
export default function ProductCard({
  product, currency, inCart, onAdd, onInc, onDec, onInfo,
  favoriteState, onToggleFavorite, onRequireMembership, onRequireVerification,
  // Product attributes / Search (Sep 2026). `highlight` rings the card
  // for a moment after the customer picks it from a suggestion;
  // `cardRef` lets the parent scroll it into view. Both optional — a
  // caller that does not pass them gets today's behaviour exactly.
  highlight = false, cardRef,
  // Customer-selectable spice level (Sep 2026, restored). The product's
  // configured `spice_level` is the DEFAULT of a customer choice, not a
  // fixed description: a card whose product has one renders a compact
  // L / M / H group preset to it, and what the customer picks travels
  // with the cart line to the kitchen. A product with no level renders
  // no selector at all, and nothing here ever invents one.
  //
  // `spiceEnabled` is the CATALOG's existing switch
  // (`attribute_config.spice_enabled`) — off, and no card offers a
  // selector however its products are configured.
  spiceEnabled = true,
  // The cart indexed by `(product_id, spice_level)`. Optional: without
  // it the card falls back to `inCart`, which is today's behaviour.
  cartLines,
}) {
  const available = product.available !== false && product.active !== false;
  // Inventory Phase 3 (D6) — the server marks only TRACKED products, and
  // only in-stock / out-of-stock (never a count). Untracked products carry
  // no field and render exactly as before. Checkout stays authoritative.
  const outOfStock = product.inventory_stock_state === "out_of_stock";
  const popular = !!product.popular || !!product.featured;
  const badges = useMemo(() => dietaryBadgesFor(product), [product]);
  // Image source (Sep 2026 correction). `_img` is the pre-cleaned URL
  // the main catalog mapping attaches in `RetailCustomer.jsx`; it is NOT
  // attached on every path that renders a card. Favorites in particular
  // hands this component the raw server payload straight from
  // `useRetailFavorites`, which carries `image_url` and no `_img` — so
  // every favorited product rendered with no photo at all, while the
  // same product showed one in the menu.
  //
  // Resolving both here fixes that for Favorites and for any future
  // consumer, and is the same fallback `ProductInfoModal` has always
  // had (`product._img || cleanImageUrl(product.image_url)`).
  const [imageBroken, setImageBroken] = useState(false);
  useEffect(() => { setImageBroken(false); }, [product.id, product._img, product.image_url]);
  const cardImage = imageBroken
    ? ""
    : (product._img || cleanImageUrl(product.image_url) || "");
  // Product attributes (Sep 2026) — Brand, pack size and spice level.
  // Every one is absent-by-default, and each block below renders ONLY
  // when its value exists, so a plain restaurant card is visually
  // identical to what it is today. `highlight` briefly rings the card
  // after a customer picks it from a search suggestion.
  const packLabel = useMemo(() => formatPackLabel(product), [product]);
  const spice = useMemo(() => spiceIndicatorFor(product), [product]);
  const brand = (product.brand || "").trim();
  // The configured level is the default selection; `null` when the
  // product is "Not set", which is what suppresses the selector.
  const defaultSpice = spice ? spice.code : null;
  const [chosenSpice, setChosenSpice] = useState(defaultSpice);
  useEffect(() => { setChosenSpice(defaultSpice); }, [product.id, defaultSpice]);
  // Offered only for an available product that actually has a level, on
  // a catalog whose operator turned Spice level on. An unavailable
  // product keeps the read-only chip: there is nothing to choose.
  const showsSpicePicker = Boolean(spiceEnabled && defaultSpice && available);
  // The stepper must act on the line matching THIS card's current
  // selection: a card showing Hot steps the Hot line, never the Low
  // one, even though both carry the same `product_id`. Without a
  // `cartLines` index (or without a selection to make) this is exactly
  // the `inCart` the caller passed.
  const activeLine = useMemo(() => {
    if (!showsSpicePicker || !cartLines) return inCart;
    return cartLines.get(cartLineKey(product.product_id || product.id, chosenSpice)) || null;
  }, [showsSpicePicker, cartLines, product.product_id, product.id, chosenSpice, inCart]);
  // Rapid-repeated-click guard — a toggle already in flight is not
  // re-issued; the visible star state itself (driven by the parent's
  // own optimistic `favoriteState`) is the single source of truth, so
  // this only prevents piling up duplicate in-flight requests, never a
  // duplicate DATABASE record (the backend's own idempotent upsert/
  // unique index is the real guarantee — see `services/favorites.py`).
  const [toggling, setToggling] = useState(false);
  const handleStarClick = async (e) => {
    e.stopPropagation();
    if (toggling) return;
    if (favoriteState === "verify") {
      (onRequireVerification || onRequireMembership)?.();
      return;
    }
    if (favoriteState === "locked") {
      onRequireMembership && onRequireMembership();
      return;
    }
    if (!onToggleFavorite) return;
    setToggling(true);
    try {
      await onToggleFavorite(product);
    } finally {
      setToggling(false);
    }
  };
  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      className={
        "bg-[#fdfbf0] text-stone-900 rounded-md border flex flex-col p-3 relative shadow-sm hover:shadow-md transition " +
        (highlight
          ? "border-orange-500 ring-2 ring-orange-400 ring-offset-1"
          : "border-emerald-900/15")
      }
      data-testid={`retail-product-${product.id}`}
      data-highlighted={highlight ? "true" : undefined}
    >
      {popular && (
        <div className="absolute -top-1 left-2 text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
          ⭐ Popular
        </div>
      )}
      {favoriteState && (
        <button
          type="button"
          onClick={handleStarClick}
          data-testid={`retail-favorite-${product.id}`}
          aria-label={
            favoriteState === "verify"
              ? "Verify your phone to save favorites"
              : favoriteState === "locked"
              ? "Join membership to save favorites"
              : favoriteState === "filled" ? "Remove from Favorites" : "Add to Favorites"
          }
          aria-pressed={favoriteState === "filled"}
          title={
            favoriteState === "verify"
              ? "Verify to save Favorites"
              : favoriteState === "locked" ? "Membership required to save Favorites" : undefined
          }
          className={
            /* Touch target widened to 44px (Sep 2026) via padding while
               the visible pill stays the same size — the previous 24px
               hit area was well under the accessible minimum on the
               kiosk and on phones. */
            "absolute -top-2 right-1 w-11 h-11 p-2.5 grid place-items-center rounded-full transition " +
            (favoriteState === "filled"
              ? "text-orange-500"
              : favoriteState === "locked" || favoriteState === "verify"
              ? "text-stone-400"
              : "text-stone-500 hover:text-orange-500")
          }
        >
          <span
            className={
              "w-6 h-6 grid place-items-center rounded-full border " +
              (favoriteState === "filled"
                ? "bg-orange-500 text-white border-orange-500"
                : "bg-white/90 border-stone-300")
            }
          >
            <Star className="w-3.5 h-3.5" fill={favoriteState === "filled" ? "currentColor" : "none"} />
          </span>
        </button>
      )}
      {cardImage ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInfo && onInfo(product); }}
          className="block w-full mb-2"
          title="View full image"
          data-testid={`retail-product-img-btn-${product.id}`}
        >
          <img
            src={cardImage}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-24 sm:h-28 rounded-md object-cover bg-white hover:opacity-90 transition-opacity"
            onError={() => setImageBroken(true)}
            data-testid={`retail-product-img-${product.id}`}
          />
        </button>
      ) : (
        /* One consistent placeholder for BOTH "no photo" and "the photo
           failed to load", at exactly the image's own dimensions. Before
           this, a missing image rendered nothing and a broken one was
           hidden with `display:none`, so cards in the same row ended up
           different heights — visible now that some products carry a
           deliberately uploaded photo and others do not. */
        <div
          className="w-full h-24 sm:h-28 rounded-md mb-2 bg-stone-100 border border-stone-200 grid place-items-center"
          aria-hidden="true"
          data-testid={`retail-product-img-placeholder-${product.id}`}
        >
          <ImageOff className="w-5 h-5 text-stone-300" />
        </div>
      )}
      {brand && (
        <div
          className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/80 truncate"
          data-testid={`retail-brand-${product.id}`}
        >
          {brand}
        </div>
      )}
      {/* Name row: [ Product Name ] [i] ........ (star, absolute, right)
          The details button belongs beside the name — that is what it
          describes. The Favorites star is absolutely positioned in the
          card's top-right corner and is 44px square, so it hangs down
          across this row; `pr-14` reserves that whole footprint when a
          star renders, which keeps the name from being printed
          underneath it AND keeps the details button's own 44px target
          clear of the star's. The two were measured overlapping
          completely before this reservation existed. */}
      <div className={"flex items-start gap-1.5" + (favoriteState ? " pr-14" : "")}>
        <h3 className="text-sm font-semibold leading-snug min-w-0">{product.name}</h3>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInfo && onInfo(product); }}
          data-testid={`retail-info-${product.id}`}
          /* 44px tappable target that costs NO layout width: the
             pseudo-element grows the hit area around the 14px icon
             without moving the name beside it or changing the row's
             height. Padding + negative margin would do the same to the
             box but would drag the icon out of line with the first
             line of a wrapping name. */
          className="relative shrink-0 mt-0.5 text-stone-400 hover:text-stone-700 before:absolute before:-inset-[15px] before:content-['']"
          title="View details"
          aria-label="Product details"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>
      {(badges.length > 0 || spice) && (
        <div className="mt-1 flex flex-wrap gap-1 items-center" data-testid={`retail-badges-${product.id}`}>
          {badges.map((b) => (
            <span key={b.k} title={b.title} className={"text-[9px] font-bold px-1.5 py-0.5 rounded " + b.cls}>{b.k}</span>
          ))}
          {spice && !showsSpicePicker && (
            /* Icon PLUS text, never colour alone — readable on the
               kiosk without hover, and announced properly by a screen
               reader via the aria-label. */
            <span
              className={"text-[9px] font-bold px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 " + spice.cls}
              data-testid={`retail-spice-${product.id}`}
              aria-label={`Spice level: ${spice.label}`}
              title={`Spice level: ${spice.label}`}
            >
              <span aria-hidden="true">🌶</span>{spice.label}
            </span>
          )}
        </div>
      )}
      {packLabel && (
        <div
          className="mt-1 text-[11px] font-medium text-stone-600"
          data-testid={`retail-pack-${product.id}`}
        >
          {packLabel}
        </div>
      )}
      {product.description && (
        <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{product.description}</p>
      )}
      {showsSpicePicker && (
        /* Its OWN row, below the description and above the price/ADD
           row. Deliberately not in the name row and not in the price
           row: the name row carries the details "i" and is overhung by
           the Favorites star (PR #190 / #194), and the price row's
           ADD control must keep its full width on a narrow card. */
        <div className="mt-2" data-testid={`retail-spice-picker-${product.id}`}>
          <div className="text-[9px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-0.5">
            <span aria-hidden="true">🌶</span> Spice
          </div>
          <div
            className="mt-1 flex items-center gap-1"
            role="radiogroup"
            aria-label={`Spice level for ${product.name}`}
          >
            {["low", "medium", "hot"].map((code) => {
              const selected = chosenSpice === code;
              return (
                <button
                  key={code}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={SPICE_LABELS[code]}
                  title={`${SPICE_LABELS[code]} spice`}
                  data-testid={`retail-spice-${code}-${product.id}`}
                  onClick={(e) => { e.stopPropagation(); setChosenSpice(code); }}
                  className={
                    /* 28px visible, 44px tappable via the pseudo-element
                       — the same trick the details "i" uses, so the row
                       costs no extra layout height on a phone. */
                    "relative w-7 h-7 grid place-items-center rounded-md border text-[11px] font-bold transition "
                    + "before:absolute before:-inset-2 before:content-[''] "
                    + (selected
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-400")
                  }
                >
                  {SPICE_SHORT[code]}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="font-semibold text-stone-900">{formatMoney(product.price, currency)}</div>
        {!available ? (
          <span className="text-[11px] px-2 py-1 rounded-md bg-stone-200 text-stone-500">Unavailable</span>
        ) : outOfStock && !activeLine ? (
          <span className="text-[11px] px-2 py-1 rounded-md bg-stone-200 text-stone-500" data-testid={`retail-out-of-stock-${product.id}`}>Out of stock</span>
        ) : activeLine ? (
          <div className="flex items-center gap-1 bg-orange-500 text-white rounded-md px-1.5 py-0.5">
            <button type="button" onClick={() => onDec(activeLine)} aria-label="Decrease" data-testid={`retail-qty-dec-${product.id}`} className="p-0.5 hover:bg-orange-600 rounded">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="text-sm font-semibold min-w-[1ch] text-center" data-testid={`retail-qty-${product.id}`}>{activeLine.quantity}</span>
            <button type="button" onClick={() => onInc(activeLine)} aria-label="Increase" data-testid={`retail-qty-inc-${product.id}`} className="p-0.5 hover:bg-orange-600 rounded">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAdd(showsSpicePicker ? chosenSpice : null)}
            data-testid={`retail-add-${product.id}`}
            className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 active:scale-[0.97]"
          >
            ADD <Plus className="w-3 h-3" strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
